import { File, Loader, Trash2 } from "lucide-react";
import { useState } from "react";
import { Input } from "../../../../../components/ui/common/input";
import ConfirmModal from "../../../../../components/ui/widget/ConfirmModal/ConfirmModal";
import { DocumentItem } from "./hooks/useDocumentManagement";
import {
  formatDate,
  formatFileSize,
  hasDocumentError,
  isDocumentUploading
} from "./utils/documentUtils";

interface DocumentListItemProps {
  doc: DocumentItem;
  dataView?: boolean;
  onNoteChange: (id: string, note: string) => void;
  onDelete: (id: string) => void;
}

export const DocumentListItem = ({
  doc,
  dataView = false,
  onNoteChange,
  onDelete
}: DocumentListItemProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const isUploading = isDocumentUploading(doc);
  const hasError = hasDocumentError(doc);
  const isDisabled = dataView || isUploading;

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onDelete(doc.id);
    setShowDeleteConfirm(false);
  };

  return (
    <div
      className={`tw-flex tw-items-center tw-gap-6 tw-p-4 tw-rounded-md tw-transition-colors ${
        isUploading ? "tw-bg-muted" : hasError ? "tw-bg-destructive/10" : "hover:tw-bg-muted"
      }`}
    >
      {/* File Icon with Loading Indicator */}
      <div className="tw-relative tw-flex-shrink-0">
        <File className="tw-h-5 tw-w-5 tw-text-muted-foreground" />
        {isUploading && (
          <Loader className="tw-absolute tw-inset-0 tw-h-6 tw-w-6 tw-animate-spin tw-text-primary" />
        )}
      </div>

      {/* File Info - Fixed Width */}
      <div className="tw-flex-shrink-0 tw-w-48">
        <p className="tw-font-medium tw-text-foreground tw-truncate">{doc.file.name}</p>
        <p className="tw-text-xs tw-text-muted-foreground mt-1">
          {formatFileSize(doc.file.size)} · {formatDate(doc.uploadedAt)}
        </p>
        {hasError && <p className="tw-text-xs tw-text-destructive mt-1">{doc.uploadError}</p>}
      </div>

      {/* Note Input */}
      <Input
        type="text"
        placeholder="Additional notes"
        value={doc.note}
        onChange={(e: any) => onNoteChange(doc.id, e.target.value)}
        disabled={isDisabled}
        className="tw-flex-1 tw-min-w-[300px]"
      />

      {/* Delete Button */}
      <button
        onClick={handleDeleteClick}
        disabled={isDisabled}
        className="tw-p-2 tw-text-destructive hover:tw-bg-destructive hover:tw-text-white tw-rounded-md tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex-shrink-0 tw-h-fit"
      >
        <Trash2 className="tw-h-5 tw-w-5" />
      </button>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Document"
        message={`Are you sure you want to delete "${doc.file.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="destructive"
      />
    </div>
  );
};
